/**
 * VelocityTile — top-N pooled-pallet hoarders leaderboard. Renders on the
 * Balances tab below the by-type cards. Each row shows on-hand, avg age,
 * a "hoard score" badge, and the daily hire burn so the operator instantly
 * sees both the dwell-time problem and the silent revenue upside.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Lightning, ArrowClockwise, TrendUp, PaperPlaneTilt } from '@phosphor-icons/react';
import { getPalletVelocity, sendPalletRecallReminder } from '../../lib/api';
import { fmt$ } from './utils';

export default function VelocityTile() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recallingKey, setRecallingKey] = useState(null);  // `${mid}-${tid}`

  const fetchVelocity = async () => {
    setLoading(true);
    try {
      const r = await getPalletVelocity({ limit: 5 });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load velocity');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchVelocity(); }, []);

  const sendRecall = async (row) => {
    const key = `${row.merchant_id}-${row.type_id}`;
    if (!window.confirm(
      `Email a pickup reminder to ${row.merchant_name}?\n\n`
      + `They are holding ${row.on_hand} × ${row.type_code} for ${row.avg_age_days} days on average.`,
    )) return;
    setRecallingKey(key);
    try {
      const r = await sendPalletRecallReminder({
        merchant_id: row.merchant_id,
        type_id: row.type_id,
      });
      const d = r.data;
      const statusLabel = d.mocked ? 'mocked (RESEND_API_KEY not set)' : (d.status || 'sent');
      toast.success(`Reminder sent to ${d.to} · ${statusLabel}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setRecallingKey(null);
    }
  };

  // Highest hoard_score caps the bar widths so visualisation is comparative.
  const maxScore = rows.reduce((m, r) => Math.max(m, r.hoard_score || 0), 0) || 1;

  return (
    <div className="border border-border bg-card" data-testid="pallet-velocity-tile">
      <div className="px-3 py-2 border-b border-border bg-muted/40 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightning size={16} weight="fill" className="text-amber-600" />
          <h3 className="text-sm font-bold tracking-tight">Pallet velocity · top 5 pooled hoarders</h3>
        </div>
        <button onClick={fetchVelocity} className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="velocity-refresh"
          aria-label="refresh">
          <ArrowClockwise size={14} />
        </button>
      </div>

      {loading && <div className="p-6 text-center text-muted-foreground text-sm">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="p-6 text-center text-muted-foreground text-sm">
          No merchant-tagged pooled pallets on hand. Tag pallets to a merchant during receiving or via the Movements tab to start tracking dwell-time.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <table className="w-full text-sm">
          <thead className="bg-muted/20 border-b border-border">
            <tr className="text-left">
              <th className="p-2">#</th>
              <th className="p-2">Merchant · Type</th>
              <th className="p-2 text-right">On hand</th>
              <th className="p-2 text-right">Avg age</th>
              <th className="p-2">Hoard score</th>
              <th className="p-2 text-right">Daily hire burn</th>
              <th className="p-2 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const pct = maxScore > 0 ? (r.hoard_score / maxScore) * 100 : 0;
              const tone =
                r.avg_age_days >= 30 ? 'bg-rose-500' :
                r.avg_age_days >= 14 ? 'bg-amber-500' :
                'bg-sky-500';
              return (
                <tr key={`${r.merchant_id}-${r.type_id}`}
                  className="border-b border-border last:border-b-0"
                  data-testid={`velocity-row-${r.merchant_id}-${r.type_code}`}>
                  <td className="p-2 text-xs font-mono text-muted-foreground w-6">{i + 1}</td>
                  <td className="p-2">
                    <div className="font-semibold">{r.merchant_name}</div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <span style={{ background: r.color || '#475569' }} className="inline-block w-2 h-2 rounded-full" />
                      {r.type_code} · {r.type_name}
                    </div>
                  </td>
                  <td className="p-2 text-right font-mono font-bold">{r.on_hand}</td>
                  <td className={`p-2 text-right font-mono ${
                    r.avg_age_days >= 30 ? 'text-rose-700 font-bold' :
                    r.avg_age_days >= 14 ? 'text-amber-700' :
                    'text-foreground'
                  }`}>
                    {r.avg_age_days} d
                  </td>
                  <td className="p-2">
                    <div className="relative bg-muted h-5 overflow-hidden min-w-[100px]">
                      <div className={`absolute inset-0 ${tone}`} style={{ width: `${pct}%` }} />
                      <div className="absolute inset-0 flex items-center px-2 font-mono text-[11px] font-bold mix-blend-difference">
                        {r.hoard_score}
                      </div>
                    </div>
                  </td>
                  <td className="p-2 text-right font-mono text-emerald-700 font-semibold flex items-center justify-end gap-1">
                    <TrendUp size={11} weight="bold" /> {fmt$(r.daily_hire_burn)}/d
                  </td>
                  <td className="p-2 text-right">
                    <button
                      type="button"
                      onClick={() => sendRecall(r)}
                      disabled={recallingKey === `${r.merchant_id}-${r.type_id}`}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-border hover:bg-muted/40 disabled:opacity-50"
                      data-testid={`velocity-recall-${r.merchant_id}-${r.type_code}`}
                      title={`Email pickup reminder to ${r.merchant_name}`}
                    >
                      <PaperPlaneTilt size={11} weight="bold" />
                      {recallingKey === `${r.merchant_id}-${r.type_id}` ? '…' : 'Recall'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {!loading && rows.length > 0 && (
        <div className="px-3 py-2 text-[11px] text-muted-foreground border-t border-border">
          <strong>Hoard score</strong> = on-hand × avg age (days). Ranks merchants who sit longest on pooled stock.
          The <strong className="text-emerald-700">daily hire burn</strong> column shows the revenue you're already earning — every day a merchant holds pallets, your invoice grows.
          <span className="font-semibold"> Sales upside:</span> negotiate higher storage rates or pallet-pickup fees with the top rows.
        </div>
      )}
    </div>
  );
}
